import { theme } from '@prophecy/theme';
import { Dialog, Input, Select, Stack, Switch, Text } from '@prophecy/ui-v3';
import { clone, produceWithoutFreeze } from '@prophecy/utils/data';
import { uniqueID } from '@prophecy/utils/uniqueID';
import React, { useId, useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';

import { useImportState } from '../../useImportState';
import { SelectFooter } from '../utils';

type PsetFormType = {
  pipelineIndex: number;
  name: string;
  isDefaultPset: boolean;
  configIndex?: number;
};

export const ChoosePsetConfig = ({ toggle, name }: { toggle: () => void; name: string }) => {
  const pipelineDefaultId = useId();
  const {
    transpilerState: {
      transpilerEntityTypes: { pipelines }
    },
    actions: { updateTranspiledPipeline }
  } = useImportState();

  const {
    control,
    getValues,
    watch,
    reset,
    setValue,
    formState: { errors, isValid }
  } = useForm<PsetFormType>({
    mode: 'all',
    defaultValues: {
      pipelineIndex: pipelines.length === 1 ? 0 : -1,
      name,
      isDefaultPset: true,
      configIndex: -1
    }
  });

  const currentPipelineIndex = watch('pipelineIndex');
  useEffect(() => {
    if (currentPipelineIndex === -1 && pipelines.length === 1) {
      setValue('pipelineIndex', 0);
    }
  }, [pipelines, currentPipelineIndex, setValue]);

  const pipelineOptions = pipelines.map((pipeline, i) => ({ label: pipeline.name || pipeline.filePath, value: i }));
  const isConfigPset = !watch('isDefaultPset');
  const configs = pipelines.find((pipeline, i) => i === currentPipelineIndex)?.configurations || [];
  const configOptions = configs.map((config, i) => ({ value: i, label: config.name || 'config' }));
  const configIndex = watch('configIndex') as number;
  const onSubmit = () => {
    const formData = getValues();
    const pipelineIndex = formData.pipelineIndex;
    const currentPipeline = pipelines[pipelineIndex];
    if (formData.isDefaultPset) {
      const transpilerOptions = currentPipeline.transpilerOptions || { pathsToBeReplaced: [], targetPsets: [] };
      const targetPsets = clone(transpilerOptions.targetPsets || []);
      targetPsets.push(formData.name);
      updateTranspiledPipeline(pipelineIndex, {
        ...currentPipeline,
        transpilerOptions: { ...transpilerOptions, targetPsets }
      });
    } else {
      const configurations = currentPipeline.configurations || [];
      const config = configurations?.[configIndex] || { name: formData.name, configParams: { psets: [] } };
      const psets = clone(config.configParams?.psets || []);
      psets.push(formData.name);
      const updatedConfigs = produceWithoutFreeze(configurations, (draft) => {
        draft[configIndex] = { ...config, configParams: { ...config.configParams, psets } };
      });
      updateTranspiledPipeline(pipelineIndex, {
        ...currentPipeline,
        configurations: updatedConfigs
      });
    }
    toggle();
    reset();
  };

  const createConfig = () => {
    const formData = getValues();
    const pipelineIndex = formData.pipelineIndex;
    const currentPipeline = pipelines[pipelineIndex];
    const configurations = currentPipeline.configurations || [];
    const configName = uniqueID(
      'config_',
      configurations.map((config) => config.name)
    );
    const updatedConfigs = produceWithoutFreeze(configurations, (draft) => {
      draft.push({ name: configName, enabled: true, configParams: { psets: [] } });
    });
    updateTranspiledPipeline(pipelineIndex, {
      ...currentPipeline,
      configurations: updatedConfigs
    });
  };

  return (
    <Dialog
      onOk={onSubmit}
      okButton={{ children: 'Add Parameter Set', variant: 'primary', disabled: !isValid || currentPipelineIndex < 0 }}
      size='l'
      closable={true}
      open={true}
      onClose={toggle}
      title={`Add Parameter Set`}
      data-test-id='choose-pset-config-dialog'>
      <Stack gap={theme.spaces.x24} width='100%' height='100%' padding={`${theme.spaces.x24} ${theme.spaces.x4}`}>
        <Controller
          render={({ field }) => (
            <Input
              placeholder='Parameter Set Name'
              {...field}
              required
              variant={errors.name ? 'error' : 'primary'}
              helpText={errors.name ? errors.name?.message : ``}
              data-test-id='choose-pset-config-pset-name-input'
            />
          )}
          control={control}
          name='name'
          rules={{
            required: 'Name is required'
          }}
        />
        <Controller
          render={({ field }) => (
            <Select
              label='Select Pipeline'
              {...field}
              showSearch={true}
              value={field.value === -1 || field.value === undefined ? 'Select Pipeline' : field.value}
              options={pipelineOptions}
              data-test-id='transpiler-choose-pset-pipeline-dropdown'
              variant={errors.pipelineIndex ? 'error' : 'primary'}
              helpText={
                errors.pipelineIndex ? (
                  errors.pipelineIndex?.message
                ) : (
                  <Stack direction='horizontal' gap={theme.spaces.x2}>
                    <Text level='xs' tone={theme.colors.gray400}>
                      Choose the Pipeline to add the psets.
                    </Text>
                  </Stack>
                )
              }
            />
          )}
          rules={{
            required: 'Pipeline is required'
          }}
          control={control}
          name='pipelineIndex'
        />
        <Controller
          render={({ field }) => (
            <Stack direction='horizontal' gap={theme.spaces.x12}>
              <Switch
                {...field}
                data-test-id='choose-pset-config-default-switch'
                aria-labelledby={pipelineDefaultId}
                onChange={(checked) => {
                  if (!checked) {
                    setValue('configIndex', -1);
                  }
                  setValue('isDefaultPset', checked, {
                    shouldValidate: true
                  });
                }}
                checked={Boolean(field.value)}
              />
              <Stack gap={theme.spaces.x2}>
                <Text id={pipelineDefaultId} level='xs' tone={theme.colors.gray700}>
                  Pipeline Default Parameter Set
                </Text>
              </Stack>
            </Stack>
          )}
          control={control}
          name='isDefaultPset'
        />

        {isConfigPset && (
          <Controller
            render={({ field }) => (
              <Select
                label='Select Configuration'
                {...field}
                required
                options={
                  configOptions.length > 0 ? configOptions : [{ label: 'No Configs', value: '', disabled: true }]
                }
                showSearch={true}
                disabled={getValues('pipelineIndex') === -1 || getValues('pipelineIndex') === undefined}
                data-test-id='choose-pset-config-dropdown'
                value={field.value === -1 ? 'Configuration..' : field.value}
                variant={errors.configIndex ? 'error' : 'primary'}
                footer={<SelectFooter onClick={createConfig} btnText={'Create New'} />}
                helpText={
                  errors.configIndex ? (
                    errors.configIndex?.message
                  ) : (
                    <Stack direction='horizontal' gap={theme.spaces.x2}>
                      <Text level='xs' tone={theme.colors.gray400}>
                        Choose the Pipeline Configuration to add the psets.
                      </Text>
                    </Stack>
                  )
                }
              />
            )}
            rules={{
              validate: () => (isConfigPset && configIndex === -1 ? 'Configuration field is required' : undefined)
            }}
            control={control}
            name='configIndex'
          />
        )}
      </Stack>
    </Dialog>
  );
};
